import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { API_BASE, api, formatApiErrorDetail } from "@/lib/api";
import { saveCoupon } from "@/lib/couponStore";
import { Reveal } from "@/components/Reveal";
import CouponTicket from "@/components/landing/CouponTicket";

const NEON_IMG = "/assets/baianeira/baianeira-neon.jpeg";

const inputCls =
  "w-full border border-white/15 bg-black/50 px-4 py-3.5 text-base text-[#F5F2E9] placeholder:text-[#555] focus:border-[#D4AF37]";
const labelCls = "mb-2 block text-[10px] font-semibold tracking-[0.3em] text-[#D4AF37]";

const todayIt = () => new Date().toLocaleDateString("sv-SE");

export const OfferForm = ({ settings, coupon, setCoupon, savedCoupon, adParams }) => {
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [resDate, setResDate] = useState("");
  const [resTime, setResTime] = useState("20:00");
  const [guests, setGuests] = useState("2");
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const submittedRef = useRef(false);
  const formValuesRef = useRef({ first, last, phone, resDate, resTime, guests, privacy });
  formValuesRef.current = { first, last, phone, resDate, resTime, guests, privacy };

  const sendAbandoned = () => {
    if (submittedRef.current) return;
    const v = formValuesRef.current;
    if (!v.privacy) return;
    if ((v.phone || "").replace(/\D/g, "").length < 6) return;
    try {
      if (sessionStorage.getItem("baianeira_abandoned_sent")) return;
      sessionStorage.setItem("baianeira_abandoned_sent", "1");
    } catch {
      /* storage non disponibile */
    }
    try {
      // fetch nativo con keepalive: axios non supporta keepalive e la richiesta
      // verrebbe interrotta alla chiusura della pagina
      fetch(`${API_BASE}/api/abandoned`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name: v.first || "",
          last_name: v.last || "",
          phone: v.phone || "",
          res_date: v.resDate || "",
          res_time: v.resTime || "",
          guests: Number(v.guests) || 0,
          privacy_accepted: true,
        }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* ignora */
    }
  };

  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") sendAbandoned();
    };
    document.addEventListener("visibilitychange", onHide);
    window.addEventListener("pagehide", sendAbandoned);
    return () => {
      document.removeEventListener("visibilitychange", onHide);
      window.removeEventListener("pagehide", sendAbandoned);
    };
  }, []);

  useEffect(() => {
    if (!resDate) {
      setSlots([]);
      return;
    }
    setSlotsLoading(true);
    api
      .get("/slots", { params: { date: resDate } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [resDate]);

  useEffect(() => {
    if (slots.length && !slots.includes(resTime)) setResTime(slots[0]);
  }, [slots]);

  const promoEnded =
    !!settings.promo_end_date && new Date(`${settings.promo_end_date}T23:59:59Z`) <= new Date();

  const submit = async (e) => {
    e.preventDefault();
    if (!first.trim() || !last.trim() || !email.trim() || !phone.trim() || !resDate) {
      toast.error("Compila tutti i campi obbligatori.");
      return;
    }
    if (!privacy) {
      toast.error("Accetta l'informativa privacy per continuare.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/reservations", {
        first_name: first,
        last_name: last,
        email,
        phone,
        res_date: resDate,
        res_time: resTime,
        guests: Number(guests) || 2,
        privacy_accepted: privacy,
        marketing_consent: marketing,
        gclid: adParams?.gclid || "",
        fbclid: adParams?.fbclid || "",
      });
      const reservationData = { ...data.reservation, email_sent: data.email_sent, already_issued: data.already_issued };
      saveCoupon(data.reservation);
      setCoupon(reservationData);
      submittedRef.current = true;
      try {
        api.post("/visits/event", { event: "form_submit" }).catch(() => {});
      } catch {
        /* ignora */
      }
      if (typeof window.gtag === "function") {
        window.gtag("event", "generate_lead");
        window.gtag("event", "submit_lead_form");
      }
      if (data.already_issued) toast.info("Avevi già una prenotazione per questa data: te la mostriamo qui sotto.");
      else toast.success("Prenotazione registrata! Ti arriva per email.");
      if (data.email_sent === false)
        toast.warning("Prenotazione registrata! L'invio email è in ritardo: salva il biglietto qui sotto.");
      window.setTimeout(() => {
        const el = document.querySelector("#coupon-form");
        if (window.__lenis) window.__lenis.scrollTo(el);
        else el?.scrollIntoView({ behavior: "smooth" });
      }, 150);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      sendAbandoned();
    }
    setLoading(false);
  };

  return (
    <section
      id="coupon-form"
      data-testid="coupon-form-section"
      className="relative overflow-hidden border-t border-[#D4AF37]/10 bg-[#0A1F14]/20 py-20 md:py-32"
    >
      <div className="relative mx-auto max-w-6xl px-5 md:px-8">
        <div className="grid items-center gap-12 md:grid-cols-[1.1fr,0.9fr] md:gap-16">
          <div>
            {savedCoupon && !coupon && (
              <div
                data-testid="saved-coupon-banner"
                className="glass mb-6 flex items-center justify-between gap-4 border-[#D4AF37]/40 p-5"
              >
                <p className="text-[10px] font-bold leading-relaxed tracking-[0.2em] text-[#F3E5AB]">
                  HAI GIÀ UNA PRENOTAZIONE SALVATA SU QUESTO TELEFONO
                </p>
                <button
                  data-testid="view-saved-coupon-button"
                  onClick={() => setCoupon({ ...savedCoupon, restored: true })}
                  className="shrink-0 bg-[#D4AF37] px-4 py-2.5 text-[10px] font-bold tracking-[0.2em] text-[#050505] transition-colors hover:bg-[#F3E5AB]"
                >
                  GUARDA
                </button>
              </div>
            )}
            {promoEnded ? (
              <div className="glass p-7 md:p-12" data-testid="promo-ended-message">
                <p className="text-[10px] font-semibold tracking-[0.4em] text-[#D4AF37] md:text-xs">
                  OFFERTA A TEMPO LIMITATO
                </p>
                <h2 className="mt-4 font-display text-3xl text-[#F5F2E9] md:text-4xl">
                  L'offerta è terminata
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
                  La promozione del {settings.discount_percent}% sul menù alla
                  carta durava una sola settimana ed è ora conclusa.
                </p>
              </div>
            ) : !coupon ? (
              <Reveal>
                <div className="glass p-7 md:p-12">
                  <p className="text-[10px] font-semibold tracking-[0.4em] text-[#D4AF37] md:text-xs">
                    PRENOTA IL TUO TAVOLO
                  </p>
                  <h2 className="mt-4 font-display text-3xl text-[#F5F2E9] md:text-4xl">
                    Prenota con il {settings.discount_percent}% di sconto
                  </h2>
                  <p
                    data-testid="form-limited-note"
                    className="mt-3 inline-block border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-3 py-1.5 text-[9px] font-bold tracking-[0.25em] text-[#F3E5AB]"
                  >
                    FINO A POSTI LIMITATI · PER ASSAGGIARE I NUOVI PIATTI
                  </p>
                  <p
                    data-testid="form-price-anchor"
                    className="mt-3 text-sm font-semibold leading-relaxed text-[#F3E5AB]"
                  >
                    In media 35€ a persona.
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-[#A3A3A3]">
                    Scegli giorno e orario: ricevi subito la conferma della prenotazione via email. Sconto sul menù alla carta.
                  </p>

                  <form data-testid="reservation-form" onSubmit={submit} className="mt-8 space-y-5">
                    <div className="grid gap-5 md:grid-cols-2">
                      <label className="block">
                        <span className={labelCls}>NOME *</span>
                        <input
                          data-testid="reservation-first-name-input"
                          type="text"
                          required
                          maxLength={50}
                          autoComplete="given-name"
                          value={first}
                          onChange={(e) => setFirst(e.target.value)}
                          className={inputCls}
                        />
                      </label>
                      <label className="block">
                        <span className={labelCls}>COGNOME *</span>
                        <input
                          data-testid="reservation-last-name-input"
                          type="text"
                          required
                          maxLength={50}
                          autoComplete="family-name"
                          value={last}
                          onChange={(e) => setLast(e.target.value)}
                          className={inputCls}
                        />
                      </label>
                    </div>

                    <label className="block">
                      <span className={labelCls}>EMAIL *</span>
                      <input
                        data-testid="reservation-email-input"
                        type="email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={inputCls}
                      />
                    </label>

                    <label className="block">
                      <span className={labelCls}>TELEFONO *</span>
                      <input
                        data-testid="reservation-phone-input"
                        type="tel"
                        required
                        maxLength={25}
                        autoComplete="tel"
                        placeholder="328 000 0000"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        className={inputCls}
                      />
                    </label>

                    <div className="grid gap-5 md:grid-cols-3">
                      <label className="block">
                        <span className={labelCls}>DATA *</span>
                        <input
                          data-testid="reservation-date-input"
                          type="date"
                          required
                          min={todayIt()}
                          value={resDate}
                          onChange={(e) => setResDate(e.target.value)}
                          className={inputCls}
                        />
                      </label>
                      <label className="block">
                        <span className={labelCls}>ORARIO *</span>
                        {slotsLoading ? (
                          <div className={`${inputCls} text-[#555]`} data-testid="reservation-time-loading">
                            CARICAMENTO ORARI...
                          </div>
                        ) : resDate && slots.length === 0 ? (
                          <div data-testid="reservation-closed-note" className={`${inputCls} text-[#E0B75F]`}>
                            Chiusi il giorno selezionato: scegli un'altra data.
                          </div>
                        ) : (
                          <select
                            data-testid="reservation-time-select"
                            value={resTime}
                            onChange={(e) => setResTime(e.target.value)}
                            className={inputCls}
                            disabled={!resDate}
                          >
                            {slots.length === 0 && (
                              <option value="" className="bg-[#050505]">
                                SCEGLI LA DATA
                              </option>
                            )}
                            {slots.map((o) => (
                              <option key={o} value={o} className="bg-[#050505]">
                                {o}
                              </option>
                            ))}
                          </select>
                        )}
                      </label>
                      <label className="block">
                        <span className={labelCls}>PERSONE *</span>
                        <select
                          data-testid="reservation-guests-select"
                          value={guests}
                          onChange={(e) => setGuests(e.target.value)}
                          className={inputCls}
                        >
                          {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                            <option key={n} value={n} className="bg-[#050505]">
                              {n} {n === 1 ? "persona" : "persone"}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>

                    <label className="flex cursor-pointer items-start gap-3 py-1 text-xs leading-relaxed text-[#A3A3A3]">
                      <input
                        data-testid="reservation-privacy-checkbox"
                        type="checkbox"
                        checked={privacy}
                        onChange={(e) => setPrivacy(e.target.checked)}
                        className="mt-0.5 h-6 w-6 shrink-0 cursor-pointer appearance-none border-2 border-[#D4AF37]/60 bg-black/60 checked:border-[#D4AF37] checked:bg-[#D4AF37]"
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23050505' d='M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z'/%3E%3C/svg%3E\")",
                          backgroundSize: "16px 16px",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                        }}
                      />
                      <span className="select-none">
                        Ho letto e accetto l'{" "}
                        <a data-testid="privacy-link" href="/privacy" className="text-[#D4AF37] underline underline-offset-2">
                          informativa privacy
                        </a>{" "}
                        per il trattamento dei miei dati.
                      </span>
                    </label>

                    <label className="flex cursor-pointer items-start gap-3 py-1 text-xs leading-relaxed text-[#A3A3A3]">
                      <input
                        data-testid="reservation-marketing-checkbox"
                        type="checkbox"
                        checked={marketing}
                        onChange={(e) => setMarketing(e.target.checked)}
                        className="mt-0.5 h-6 w-6 shrink-0 cursor-pointer appearance-none border-2 border-[#D4AF37]/60 bg-black/60 checked:border-[#D4AF37] checked:bg-[#D4AF37]"
                        style={{
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23050505' d='M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z'/%3E%3C/svg%3E\")",
                          backgroundSize: "16px 16px",
                          backgroundPosition: "center",
                          backgroundRepeat: "no-repeat",
                        }}
                      />
                      <span className="select-none">Desidero ricevere future offerte commerciali di Baianeira Sushi Fusion (facoltativo).</span>
                    </label>

                    <button
                      data-testid="reservation-submit-button"
                      type="submit"
                      disabled={loading}
                      className="gold-glow w-full bg-[#D4AF37] py-4 text-xs font-bold tracking-[0.25em] text-[#050505] transition-colors duration-300 hover:bg-[#F3E5AB] active:scale-[0.98] disabled:opacity-50 md:text-sm"
                    >
                      {loading ? "PRENOTAZIONE..." : `PRENOTA CON IL ${settings.discount_percent}% DI SCONTO`}
                    </button>
                    <p
                      data-testid="form-scarcity-note"
                      className="border border-[#D4AF37]/50 bg-[#D4AF37]/10 px-4 py-3 text-center text-[11px] font-bold leading-relaxed tracking-[0.15em] text-[#F3E5AB]"
                    >
                      SONO RIMASTI SOLO 6-7 POSTI CON LO SCONTO DEL {settings.discount_percent}% · IL RESTO È SOLD OUT
                    </p>
                  </form>
                </div>
              </Reveal>
            ) : (
              <CouponTicket
                data={coupon}
                bookingUrl={settings.booking_url}
                discount={coupon.discount_percent || settings.discount_percent}
              />
            )}
          </div>

          <Reveal delay={0.15}>
            <div className="relative mx-auto max-w-sm pb-8 pr-8 md:max-w-none md:pb-12 md:pr-12">
              <div className="absolute -left-3 -top-3 hidden h-full w-full border border-[#D4AF37]/40 md:block" />
              <img
                src={NEON_IMG}
                alt="Insegne neon Baianeira"
                className="relative aspect-[4/5] w-full object-cover"
              />
              <p className="absolute bottom-0 left-8 bg-[#050505] px-4 py-3 text-[9px] tracking-[0.35em] text-[#D4AF37] md:left-12">
                BENVENUTI DA BAIANEIRA
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default OfferForm;
